import { useEffect, useRef, useState } from "react";

// Takeoff snapshots: named checkpoints of the revision's rooms, drawn conditions,
// edge overrides and each drawing's scale / position. Save one before a risky
// edit; restore it to put those back. Estimate settings, criteria, room elements,
// equipment and the drawing files are not part of a snapshot. Every restore first
// saves an automatic snapshot of the current state, so it can itself be undone.

const fmtDate = (d) => { if (!d) return ""; const x = new Date(d); return Number.isNaN(+x) ? "" : x.toLocaleString(); };
const defaultName = () => `Snapshot ${new Date().toISOString().slice(0, 16).replace("T", " ")}`;

export function restoreSummary(r) {
  if (!r) return "Restored.";
  const parts = [`${r.RoomsUpdated ?? 0} room(s) put back`];
  if (r.RoomsRemoved) parts.push(`${r.RoomsRemoved} added since removed`);
  if (r.RoomsRecreated) parts.push(`${r.RoomsRecreated} deleted since re-created`);
  parts.push(`${r.ConditionsRestored ?? 0} drawn condition(s)`);
  if (r.ConditionsSkipped) parts.push(`${r.ConditionsSkipped} skipped (their room element no longer exists)`);
  return `Restored: ${parts.join(", ")}.`;
}

export default function TakeoffSnapshotsMenu({ loadSnapshots, onSave, onRestore, onDelete, isLocked, disabled }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const ref = useRef(null);

  async function refresh() {
    try { setRows(await loadSnapshots()); setError(""); }
    catch (e) { setError(e.message || "Could not load snapshots"); }
  }
  useEffect(() => { if (open) { setName(defaultName()); refresh(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  async function save() {
    if (!name.trim()) { setError("Give the snapshot a name."); return; }
    setBusy(true); setError(""); setMsg("");
    try { await onSave({ name: name.trim(), note: note.trim() }); setNote(""); setMsg("Snapshot saved."); await refresh(); setName(defaultName()); }
    catch (e) { setError(e.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function restore(s) {
    const ok = window.confirm(
      [
        `Restore "${s.SnapshotName}" (saved ${fmtDate(s.CreatedAt)})?`,
        "This puts the rooms, drawn conditions, edge overrides and each drawing's scale and position back to how they were then. Rooms and conditions added since are removed; rooms deleted since are re-created (their criteria and elements are not brought back).",
        "Estimate settings, criteria, room elements, equipment and the drawing files are not changed.",
        "An automatic snapshot of the current state is saved first, so you can undo this restore.",
      ].join("\n\n")
    );
    if (!ok) return;
    setBusy(true); setError(""); setMsg("");
    try { const r = await onRestore(s.SnapshotID); setMsg(restoreSummary(r)); await refresh(); }
    catch (e) { setError(e.message || "Restore failed"); }
    finally { setBusy(false); }
  }

  async function remove(s) {
    if (!window.confirm(`Delete the snapshot "${s.SnapshotName}"? This can't be undone.`)) return;
    setBusy(true); setError("");
    try { await onDelete(s.SnapshotID); await refresh(); }
    catch (e) { setError(e.message || "Delete failed"); }
    finally { setBusy(false); }
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" onClick={() => setOpen(o => !o)} disabled={disabled}
        title="Save a named checkpoint of the takeoff, or restore an earlier one"
        style={{ height: 30, padding: "0 12px", whiteSpace: "nowrap", fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", color: "var(--text2)" }}>
        📸 Snapshots ▾
      </button>
      {open && (
        <div role="dialog" aria-label="Takeoff snapshots" style={{ position: "absolute", top: 34, right: 0, zIndex: 30, width: 400, maxHeight: 460, overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.22)", padding: 10, fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Save a snapshot of the takeoff</div>
          <input value={name} onChange={e => setName(e.target.value)} aria-label="Snapshot name" placeholder="Name (e.g. Before moving the gowning rooms)"
            style={{ width: "100%", boxSizing: "border-box", height: 28, padding: "0 8px", border: "1px solid var(--cs-border)", borderRadius: 6, fontSize: 12.5, background: "var(--surface)", color: "var(--text)" }} />
          <input value={note} onChange={e => setNote(e.target.value)} aria-label="Snapshot note" placeholder="Note (optional)"
            style={{ width: "100%", boxSizing: "border-box", height: 28, padding: "0 8px", marginTop: 4, border: "1px solid var(--cs-border)", borderRadius: 6, fontSize: 12.5, background: "var(--surface)", color: "var(--text)" }} />
          <button type="button" onClick={save} disabled={busy}
            style={{ marginTop: 6, height: 28, padding: "0 12px", fontSize: 12, fontWeight: 700, border: "1px solid var(--cs-blue)", borderRadius: 6, background: "var(--cs-blue)", color: "#fff", cursor: busy ? "default" : "pointer" }}>Save snapshot</button>
          <span style={{ marginLeft: 8, color: "var(--text3)" }}>Rooms, drawn conditions, edge overrides, drawing scale/position.</span>

          {msg && <div role="status" style={{ marginTop: 8, color: "#166534" }}>{msg}</div>}
          {error && <div role="alert" style={{ marginTop: 8, color: "#b91c1c" }}>{error}</div>}

          <div style={{ fontWeight: 700, margin: "12px 0 4px" }}>Snapshots</div>
          {rows === null && !error && <div style={{ color: "var(--text3)" }}>Loading…</div>}
          {rows && rows.length === 0 && <div style={{ color: "var(--text3)" }}>No snapshots yet.</div>}
          {rows && rows.map(s => (
            <div key={s.SnapshotID} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid var(--cs-border)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                  {s.SnapshotName}{s.Kind === "auto_before_restore" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "#e0e7ff", color: "#3730a3" }}>automatic</span>}{s.Kind === "imported" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--tint-green)", color: "#166534" }}>imported</span>}
                </div>
                <div style={{ color: "var(--text3)" }}>{fmtDate(s.CreatedAt)}{s.CreatedBy ? ` · ${s.CreatedBy}` : ""} · {s.RoomCount} rooms · {s.ConditionCount} conditions</div>
                {s.Note && <div style={{ color: "var(--text2)" }}>{s.Note}</div>}
              </div>
              <button type="button" disabled={busy || isLocked} onClick={() => restore(s)} title={isLocked ? "This revision is locked" : "Restore this snapshot"}
                style={{ fontSize: 11.5, padding: "3px 10px", border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", cursor: busy || isLocked ? "default" : "pointer", color: "var(--text2)" }}>Restore</button>
              <button type="button" disabled={busy} onClick={() => remove(s)} aria-label={`Delete snapshot ${s.SnapshotName}`}
                style={{ border: "none", background: "transparent", cursor: busy ? "default" : "pointer", color: "var(--text3)", fontSize: 14 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
